import type { OnlineEventViewDto } from '@/api/webs'
import { KnowledgeBaseLevel } from '@/types/knowledgeBase'
import { routes } from '@/utils/routes'
import { ROOM_ENTRY_OPENS_MINUTES_BEFORE_START, formatWallClockDay, formatWallClockTime, roomEntryOpens, sessionEnd } from '@/utils/webinarRoomSetup'

/** What the Room tab says above its cards (ORB-1086): the rehearsal before the session, the transcript after. */

/** `WebinarRoomLimits.FinalizeGraceMinutes`: attendance settles this long after the end, then the results email goes. */
export const FINALIZE_GRACE_MINUTES = 5

/** The rehearsal sentence up to its link to the Details tab, which the tab renders. */
export function rehearsalLead(event: Pick<OnlineEventViewDto, 'StartDateTime'>): string {
    const entry = roomEntryOpens(event.StartDateTime)
    const until = entry
        ? `Until viewers can enter at ${formatWallClockTime(entry)} on ${formatWallClockDay(entry)},`
        : `Until viewers can enter, ${ROOM_ENTRY_OPENS_MINUTES_BEFORE_START} minutes before the start,`
    return `${until} the room is a rehearsal for your team and the presenters, who join with the host links on the`
}

/**
 * When the questions go out. They ride in the results email, which goes to the web's reviewers and internal team
 * (`PostWebinarSummaryService`) once attendance settles, so it names them rather than whoever signed the web off.
 */
export function questionsEmailedText(event: Pick<OnlineEventViewDto, 'EndDateTime'>): string {
    const settles = sessionEnd(event.EndDateTime)?.add({ minutes: FINALIZE_GRACE_MINUTES })
    const when = settles ? ` after ${formatWallClockTime(settles)} on ${formatWallClockDay(settles)}` : ' once attendance settles'
    return `Questions are emailed with the results to this web’s reviewers and internal team${when}. Each question is on the asker’s lead timeline.`
}

export interface BrandGuidelinesLink {
    href: string
    /** Whether the person can change the brand there, which decides between "Edit brand" and "See brand". */
    canChange: boolean
}

/**
 * Where to change the brand the room uses, for this person. The brand comes from the company unless the workspace
 * has its own (ORB-1027): a company admin edits the company's where it comes from there, a workspace editor edits
 * the workspace's (which gives it its own on the first change), and anyone else reads the campaign's.
 */
export function brandGuidelinesLink(
    scope: { clientCompanyId: string; workspaceId: string; campaignId: string },
    rights: { isCompanyAdmin: boolean; canEdit: boolean },
    effectiveLevel: string | null | undefined,
): BrandGuidelinesLink {
    if (rights.isCompanyAdmin && effectiveLevel === KnowledgeBaseLevel.Company) {
        return { href: routes.settingsBrandGuidelines(scope.clientCompanyId), canChange: true }
    }
    const scoped = routes.scoped(scope.clientCompanyId, scope.workspaceId)
    return rights.canEdit
        ? { href: scoped.brandGuidelines(), canChange: true }
        : { href: scoped.campaignBrandGuidelines(scope.campaignId), canChange: false }
}
